/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import {
  createClientSubprojectKpis,
  updateSubprojectKpiBudget,
  upsertDerivativeKpis,
  upsertProjectKpis,
} from '@/features/projects/create-kpi/api/upsert-kpi';
import { KpiEditorSchemaInput } from '@/features/projects/create-project/validation/kpi-editor-schema';
import { usePaywall } from '@/features/subscriptions/hooks/use-paywall';
import { useProjectUsagePermissions } from '@/features/subscriptions/hooks/use-project-entitlements';
import { ProjectType } from '@/shared/types/api';
import { AppError } from '@/shared/utils/error-handling';

type UpsertKpiProps = Omit<
  UseMutationOptions<
    void,
    unknown,
    {
      org_id?: number;
      project_id: number;
      project_type: ProjectType;
      input: KpiEditorSchemaInput & { id?: number; kpi_budget_id?: string };
      type: 'edit_kpi' | 'create_kpi' | 'edit_assigned_kpi';
    }
  >,
  'mutationFn'
> & {
  project_id: number;
};

export const useUpsertKpisMutation = ({ ...props }: UpsertKpiProps) => {
  const { openPaywallFromError } = usePaywall();

  const { can_add_standard_kpi, can_add_derivative_kpi } =
    useProjectUsagePermissions(props.project_id);

  return useMutation({
    ...props,
    mutationFn: async ({ org_id, project_id, project_type, input, type }) => {
      const { variant } = input;

      if (variant === 'standard') {
        // Block standard KPI creation for all subproject types
        if (
          project_type === 'subproject_contractor' ||
          project_type === 'subproject_client'
        ) {
          if (type === 'create_kpi') {
            const appError: AppError =
              'STANDARD_KPI_NOT_ALLOWED_FOR_SUBPROJECT';
            throw appError;
          }
        }

        const { variant: _, kpi_budget_id: __, ...inputWithoutVariant } = input;

        if (type === 'edit_kpi') {
          await upsertProjectKpis([
            {
              ...inputWithoutVariant,
              owner_org_id: org_id,
              project_id,
            },
          ]);
        } else if (type === 'edit_assigned_kpi') {
          if (!input.kpi_budget_id) {
            throw new Error('Kpi budget id is required');
          }

          await updateSubprojectKpiBudget({
            budget: inputWithoutVariant.max_value,
            subproject_view_id: project_id,
            id: input.kpi_budget_id,
          });
        } else if (type === 'create_kpi') {
          if (!can_add_standard_kpi) {
            const appError: AppError = 'STANDARD_KPI_QUOTA_EXCEEDED';
            throw appError;
          }

          await upsertProjectKpis([
            {
              ...inputWithoutVariant,
              owner_org_id: org_id,
              project_id,
            },
          ]);
        }
      } else if (variant === 'derivative') {
        if (type === 'create_kpi') {
          if (!can_add_derivative_kpi) {
            const appError: AppError = 'DERIVATIVE_KPI_QUOTA_EXCEEDED';
            throw appError;
          }
        }

        const { variant: _, kpi_budget_id: __, ...inputWithoutVariant } = input;
        await upsertDerivativeKpis([
          {
            ...inputWithoutVariant,
            project_id,
          },
        ]);
      }
    },
    onError: async (error, variables, onMutateResult, context) => {
      const handled = await openPaywallFromError(error, props.project_id);
      if (!handled && props.onError) {
        props.onError(error, variables, onMutateResult, context);
      }
    },
  });
};
